/**
 * DPDP privacy UI (docs/SPEC.md decision 44): the itemised notice, the gate that asks for agreement to the current
 * notice (18+ declaration included), and the usage-statistics choice (opt-in, withdrawable in Settings → Privacy).
 */
import { date } from '../format';
import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { PRIVACY_NOTICE, type NoticeItem } from '@vw/core/src/privacy';
import { BRAND } from '@vw/core/src/brand';
import { Button, toast } from '@vw/ui';
import { api, ApiError, type PrivacyState } from '../api';
import { downloadText, stamp } from '../download';
import { analyticsChoice, setAnalyticsChoice } from '../analytics';

function Items({ title, items }: { title: string; items: NoticeItem[] }) {
  return (
    <div>
      <h3 className="text-sm font-bold">{title}</h3>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-muted-foreground"><tr><th className="py-1.5 pr-3">What</th><th className="pr-3">Why</th><th>How long</th></tr></thead>
          <tbody className="divide-y divide-border">{items.map((i) => <tr key={i.data} className="align-top"><td className="py-1.5 pr-3">{i.data}</td><td className="pr-3">{i.purpose}</td><td>{i.kept}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

/** The full notice (DPDP Rule 3): standalone, plain language, itemised. */
export function NoticeBody() {
  const n = PRIVACY_NOTICE;
  return (
    <div className="space-y-4 text-sm">
      <p>{n.fiduciary} runs {n.product} and is responsible for your personal data under India's Digital Personal Data Protection Act, 2023. This notice (version {n.version}) explains what we collect, why, and your rights.</p>
      <Items title="For your account (needed to use the service)" items={n.account} />
      <p className="text-xs text-muted-foreground"><b>Before you agree:</b> {n.beforeAgreeing}</p>
      <Items title="Usage statistics (optional; only if you allow them)" items={n.analytics} />
      <p className="text-xs text-muted-foreground"><b>Who helps us:</b> {n.processors}</p>
      <div>
        <h3 className="text-sm font-bold">Your rights</h3>
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">{n.rights.map((r) => <li key={r}>{r}</li>)}</ul>
      </div>
      <p className="text-xs">You can withdraw consent as easily as you gave it: one click in Settings → Privacy (for your account, or for usage statistics). After withdrawing account consent, your browsers and AI apps are disconnected at once and the account is erased after 30 days unless you agree again. Withdrawal doesn't affect processing done before it.</p>
      <p className="text-xs"><b>Contact for privacy requests and grievances:</b> <a className="text-primary underline" href={`mailto:${n.contact}`}>{n.contact}</a>. If you're not satisfied with our answer, you can complain to the Data Protection Board of India.</p>
      <p className="text-xs text-muted-foreground">{n.ageRule} {n.languages} Instagram creators whose public reels appear in {BRAND.name} can ask for removal at /remove.</p>
    </div>
  );
}

const PRECONSENT_KEY = 'vw.preconsent';

/** Sign-in page: the user ticked 18+ and agreed; remembered in this tab for 30 minutes. */
export function rememberSignInConsent() {
  try { sessionStorage.setItem(PRECONSENT_KEY, JSON.stringify({ version: PRIVACY_NOTICE.version, at: Date.now() })); } catch { /* storage blocked */ }
}

/** After sign-in: records the agreement ticked on the sign-in page (same tab, current notice, recent). */
export async function recordSignInConsent(): Promise<PrivacyState | null> {
  let v: { version?: string; at?: number } | null = null;
  try { v = JSON.parse(sessionStorage.getItem(PRECONSENT_KEY) ?? 'null'); sessionStorage.removeItem(PRECONSENT_KEY); } catch { v = null; }
  if (!v || v.version !== PRIVACY_NOTICE.version || !v.at || Date.now() - v.at > 30 * 60_000) return null;
  return api<PrivacyState>('/api/privacy/consent', { method: 'POST', json: { purpose: 'account', version: PRIVACY_NOTICE.version, adult: true, source: 'signin' } }).catch(() => null);
}

/** Shown until the signed-in user agrees to the current notice. Nothing else works until then (the API refuses). */
export function ConsentGate({ onAgreed, email, privacy }: { onAgreed: (p: PrivacyState) => void; email: string; privacy?: PrivacyState }) {
  const [busy, setBusy] = useState(false);
  async function accept() {
    setBusy(true);
    try {
      onAgreed(await api<PrivacyState>('/api/privacy/consent', { method: 'POST', json: { purpose: 'account', version: PRIVACY_NOTICE.version, adult: true, source: 'gate' } }));
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  }
  // Not agreeing still leaves your rights: download your data, or delete the account (no agreement needed).
  async function download() {
    try {
      const data = await api<unknown>('/api/account/export');
      downloadText(`${BRAND.slug}-account-${stamp()}.json`, JSON.stringify(data, null, 2), 'application/json');
    } catch (e) { toast.error('Could not export your data', { description: (e as Error).message }); }
  }
  async function remove() {
    const typed = window.prompt(`This deletes your account and can't be undone. Type your email (${email}) to confirm.`);
    if (!typed) return;
    try {
      await api('/api/account/delete', { method: 'POST', json: { confirm: typed } });
      location.href = '/signin';
    } catch (e) {
      if (e instanceof ApiError && e.code === 'reauth_required') { location.href = `/signin?return_to=${encodeURIComponent('/')}`; return; }
      // A running paid plan must be cancelled first; the billing portal works without agreeing.
      const portal = e instanceof ApiError && e.code === 'conflict' ? { label: 'Open billing portal', onClick: () => void openPortal() } : undefined;
      toast.error('Could not delete your account', { description: (e as Error).message, action: portal });
    }
  }
  async function openPortal() {
    try { location.href = (await api<{ url: string }>('/api/billing/portal', { method: 'POST' })).url; }
    catch (e) { toast.error('Could not open the billing portal', { description: (e as Error).message }); }
  }
  async function signOut() {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => undefined);
    location.href = '/signin';
  }
  return (
    <div className="flex min-h-screen items-start justify-center bg-background p-4 sm:p-8">
      <div className="w-full max-w-3xl space-y-5 rounded-3xl border border-border bg-card p-6 shadow-sm">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold"><ShieldCheck className="h-6 w-6 text-primary" />Your privacy</h1>
        <p className="text-sm text-muted-foreground">Please read how {BRAND.name} uses your personal data and agree to continue.</p>
        {privacy?.withdrawnAt && <p className="rounded-xl bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">You withdrew your consent on {date(privacy.withdrawnAt)}. Your account will be erased on {date(privacy.eraseAt)} unless you agree again.</p>}
        <div className="max-h-[55vh] overflow-y-auto rounded-2xl border border-border p-4"><NoticeBody /></div>
        <p className="text-sm">By choosing <b>Agree and continue</b>, you confirm you are 18 or older and agree to the processing of your personal data for your account, as described above. You can withdraw at any time in Settings → Privacy.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button loading={busy} onClick={accept}>Agree and continue</Button>
          <Button variant="secondary" onClick={signOut}>Don't agree, sign out</Button>
          <button type="button" className="text-xs text-muted-foreground underline" onClick={download}>Download my data</button>
          <button type="button" className="text-xs text-rose-600 underline" onClick={remove}>Delete my account</button>
        </div>
      </div>
    </div>
  );
}

/** The usage-statistics question, asked once (nothing is collected before a yes). */
export function AnalyticsBanner({ signedIn, onDone }: { signedIn: boolean; onDone: () => void }) {
  const choose = async (granted: boolean) => {
    setAnalyticsChoice(granted);
    if (signedIn) await api('/api/privacy/consent', { method: 'POST', json: { purpose: 'analytics', granted, source: 'banner' } }).catch(() => undefined);
    onDone();
  };
  return (
    <div role="dialog" aria-label="Usage statistics" className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl rounded-2xl border border-border bg-card p-4 text-sm shadow-lg">
      <p><b>Help us improve {BRAND.name}?</b> With your OK we count which page types are visited, where visitors come from (referring website, campaign tags), your approximate location and device, and which visit led to a sign-up or payment. This sets two analytics cookies, shared with our website. This includes which pages you open, also creator pages. It doesn't change how the app works. <a className="text-primary underline" href="/privacy-notice" target="_blank" rel="noreferrer">Privacy notice</a></p>
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={() => choose(true)}>Allow usage statistics</Button>
        <Button size="sm" variant="secondary" onClick={() => choose(false)}>No thanks</Button>
      </div>
    </div>
  );
}

/** Settings → Privacy: usage statistics (withdraw any time), nominee (s. 14), notice and grievance contact. */
export function PrivacySettings() {
  const [state, setState] = useState<PrivacyState | null>(null);
  const [nName, setNName] = useState('');
  const [nEmail, setNEmail] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<PrivacyState>('/api/privacy').then((s) => { setState(s); setNName(s.nominee?.name ?? ''); setNEmail(s.nominee?.email ?? ''); }, () => setState(null));
  }, []);
  if (!state) return null;
  const stats = analyticsChoice() === true;
  const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true || navigator.doNotTrack === '1';
  async function withdraw() {
    if (!window.confirm('Withdraw your consent? Your browsers and AI apps are disconnected now, the app stops working for you, and your account is erased in 30 days unless you agree again.')) return;
    try { await api<PrivacyState>('/api/privacy/withdraw', { method: 'POST' }); location.href = '/'; }
    catch (e) { toast.error((e as Error).message); }
  }
  async function setStats(granted: boolean) {
    setAnalyticsChoice(granted);
    try { setState(await api<PrivacyState>('/api/privacy/consent', { method: 'POST', json: { purpose: 'analytics', granted, source: 'settings' } })); toast.success(granted ? 'Usage statistics on' : 'Usage statistics off'); }
    catch (e) { toast.error((e as Error).message); }
  }
  async function saveNominee(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try { setState(await api<PrivacyState>('/api/privacy/nominee', { method: 'PUT', json: { name: nName, email: nEmail } })); toast.success(nName ? 'Nominee saved' : 'Nominee removed'); }
    catch (err) { toast.error((err as Error).message); } finally { setBusy(false); }
  }
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-col gap-3 rounded-2xl border border-border p-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1"><div className="font-bold">Usage statistics</div>
          <p className="text-xs text-muted-foreground">Count page types, where visitors come from, approximate location and device, and which visit led to a sign-up or payment (two analytics cookies; this includes which pages you open, also creator pages). Optional; turning it off stops it at once and clears what this browser stored.</p></div>
        {gpc ? <span className="text-xs text-muted-foreground">Off: your browser asks not to be tracked</span>
          : <label className="flex items-center gap-2 text-xs font-semibold"><input type="checkbox" checked={stats} onChange={(e) => setStats(e.target.checked)} aria-label="Usage statistics" />{stats ? 'On' : 'Off'}</label>}
      </div>
      <form onSubmit={saveNominee} className="space-y-2 rounded-2xl border border-border p-4">
        <div className="font-bold">Nominee</div>
        <p className="text-xs text-muted-foreground">Someone who may exercise your data rights (access, erasure) if you die or can't. Optional.</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input className="h-9 flex-1 rounded-xl border border-border bg-background px-3 text-sm" placeholder="Full name" value={nName} onChange={(e) => setNName(e.target.value)} aria-label="Nominee name" />
          <input className="h-9 flex-1 rounded-xl border border-border bg-background px-3 text-sm" placeholder="Email" type="email" value={nEmail} onChange={(e) => setNEmail(e.target.value)} aria-label="Nominee email" />
          <Button type="submit" variant="secondary" loading={busy}>Save</Button>
        </div>
      </form>
      <div className="flex flex-col gap-3 rounded-2xl border border-border p-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1"><div className="font-bold">Withdraw consent</div>
          <p className="text-xs text-muted-foreground">Stop {BRAND.name} using your data. Browsers and AI apps are disconnected at once; your account is erased after 30 days unless you agree again. To erase it now, use Delete my account below.</p></div>
        <Button variant="secondary" onClick={withdraw}>Withdraw</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        You agreed to privacy notice {state.noticeVersion}{state.consentAt ? ` on ${date(state.consentAt)}` : ''}. <a className="text-primary underline" href="/privacy-notice" target="_blank" rel="noreferrer">Read it</a>.
        {' '}Privacy requests and grievances: <a className="text-primary underline" href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a> (we reply within 90 days).
      </p>
    </div>
  );
}
